import { Button } from "@/components/ui/button";
import {
    ArDateFormField,
    ArFileFormField,
    ArForm,
    ArFormField,
    ArNumberFormField,
    ArSelectFormField,
    ArTextAreaFormField,
} from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import {
    CreateUploadPurchaseOrderSchema,
    UpdateUploadPurchaseOrderSchema,
    UploadPurchaseOrderFormSchema,
    UploadPurchaseOrderFormSchemaWithoutRefine,
} from "@/v2/schema/purchase-order";
import FindOnePurchaseOrder from "@/v2/api/purchase-order/FindOne";
import CreateUploadPurchaseOrder from "@/v2/api/purchase-order/upload/Create";
import { ClientComboBox, SupplierComboBox } from "../../ui/combo-box";
import UpdateUploadPurchaseOrder from "@/v2/api/purchase-order/upload/Update";

export default function UploadPruchaseOrderForm({
    id,
    closeOnSave,
    type,
    status,
}: {
    id?: string;
    closeOnSave: () => void;
    type:
        | "MANPOWER_PURCHASE_ORDER"
        | "PROJECT_PURCHASE_ORDER"
        | "EQUIPMENT_RENTAL_PURCHASE_ORDER"
        | "RECEIVED_PURCHASE_ORDER";
    status: "ISSUED" | "RECEIVED" | "DRAFT";
}) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ["purchase-order", id],
        queryFn: () => FindOnePurchaseOrder(id!),
        enabled: !!id,
    });

    const form = useForm<UploadPurchaseOrderFormSchema>({
        resolver: zodResolver(UploadPurchaseOrderFormSchema),
        defaultValues: {
            purchase_order_number: "",
            reference_number: "",
            project: "",
            total_amount: 0,
            supplier_id:
                status === "ISSUED"
                    ? ""
                    : "00000000-0000-0000-0000-000000000000",
            client_id:
                status === "RECEIVED"
                    ? ""
                    : "00000000-0000-0000-0000-000000000000",
            order_date: "",
            expiration_date: "",
            rate_type: "",
            description: "",
            remarks: "",
            existing_purchase_order_file_url: [],
            type: type,
            status: status,
        },
    });

    const { reset, watch } = form;

    useEffect(() => {
        if (query?.data) {
            reset({
                purchase_order_number: query?.data?.purchase_order_number || "",
                reference_number: query?.data?.reference_number || "",
                project: query?.data?.project || "",
                total_amount: query?.data?.total_amount || 0,
                client_id: query?.data?.client_id ?? null,
                supplier_id: query?.data?.supplier_id ?? null,
                order_date: query?.data?.order_date || "",
                expiration_date: query?.data?.expiration_date || "",
                rate_type: query?.data?.rate_type || "",
                description: query?.data?.description || "",
                remarks: query?.data?.remarks || "",
                existing_purchase_order_file_url:
                    query?.data?.purchase_order_file || [],
                status: query?.data?.status,
                type: query?.data?.type,
            });
        }
    }, [query?.data, reset]);

    const CreateMutation = useMutation({
        mutationFn: (data: CreateUploadPurchaseOrderSchema) =>
            CreateUploadPurchaseOrder(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: UpdateUploadPurchaseOrderSchema) =>
            UpdateUploadPurchaseOrder({ id: id!, data: data }),
    });

    const handleFormSubmit = (data: UploadPurchaseOrderFormSchema) => {
        if (id) {
            const payload = {
                description: data.description,
                remarks: data.remarks,
                order_date: data.order_date,
                expiration_date: data.expiration_date,
                project: data.project,
                purchase_order_number: data.purchase_order_number,
                reference_number: data.reference_number,
                status: data.status,
                rate_type: data.rate_type,
                supplier_id: data.supplier_id,
                client_id: data.client_id,
                type: data.type,
                total_amount: data.total_amount,
                purchase_order_file_url:
                    data.existing_purchase_order_file_url ?? [],
                file: data.file ?? null,
            };

            EditMutation.mutate(payload, {
                onError: (err) => {
                    console.error({ err });
                    toast.error("Oops! Something went wrong", {
                        description:
                            "While updating the purchase order, we encountered an issue.",
                    });
                },
                onSuccess: () => {
                    toast.success("Purchase Order updated successfully");
                    queryClient.invalidateQueries({
                        queryKey: ["purchase-order"],
                    });
                    closeOnSave();
                },
            });
        } else {
            const payload = {
                description: data.description,
                remarks: data.remarks,
                order_date: data.order_date,
                expiration_date: data.expiration_date,
                project: data.project,
                purchase_order_number: data.purchase_order_number,
                reference_number: data.reference_number,
                status: data.status,
                rate_type: data.rate_type,
                supplier_id: data.supplier_id,
                client_id: data.client_id,
                type: data.type,
                total_amount: data.total_amount,
                purchase_order_file_url: data.file || undefined,
            };

            CreateMutation.mutate(payload, {
                onError: (err) => {
                    console.error({ err });
                    toast.error("Oops! Something went wrong", {
                        description:
                            "While creating the purchase order, we encountered an issue.",
                    });
                },
                onSuccess: () => {
                    toast.success("Purchase Order created successfully");
                    queryClient.invalidateQueries({
                        queryKey: ["purchase-order"],
                    });
                    closeOnSave();
                },
            });
        }
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load asset</p>
                    <Button onClick={() => closeOnSave()}>Go Back</Button>
                </div>
            </div>
        );
    }

    const issuedDate = new Date(watch("order_date"));

    return (
        <ArForm
            form={form}
            formSchema={UploadPurchaseOrderFormSchemaWithoutRefine}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<UploadPurchaseOrderFormSchema>
                        label="Purchase Order Number"
                        name="purchase_order_number"
                        placeholder="Enter purchase order number"
                    />
                    <ArFormField<UploadPurchaseOrderFormSchema>
                        label="Reference Number"
                        name="reference_number"
                        placeholder="Enter reference number"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<UploadPurchaseOrderFormSchema>
                        label="Project"
                        name="project"
                        placeholder="Enter project name"
                    />
                    <ArNumberFormField<UploadPurchaseOrderFormSchema>
                        label="Total Amount"
                        name="total_amount"
                        placeholder="Enter total amount"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {status === "RECEIVED" && (
                        <ClientComboBox<UploadPurchaseOrderFormSchema>
                            name="client_id"
                            defaultValue={query?.data?.client || null}
                        />
                    )}

                    {status === "ISSUED" && (
                        <SupplierComboBox<UploadPurchaseOrderFormSchema>
                            name="supplier_id"
                            defaultValue={query?.data?.supplier || null}
                        />
                    )}
                    <ArSelectFormField<UploadPurchaseOrderFormSchema>
                        label="Rate Type"
                        name="rate_type"
                        options={[
                            {
                                value: "HOURLY",
                                label: "Hourly Rate",
                            },
                            {
                                value: "DAILY",
                                label: "Daily Rate",
                            },
                            {
                                value: "MONTHLY",
                                label: "Monthly Rate",
                            },
                        ]}
                        defaultValue={id ? query?.data?.rate_type : ""}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArDateFormField<UploadPurchaseOrderFormSchema>
                        label="Issued Date"
                        name="order_date"
                    />
                    <ArDateFormField<UploadPurchaseOrderFormSchema>
                        label="Expiration Date"
                        name="expiration_date"
                        fromDate={issuedDate}
                    />
                </div>
                <ArFileFormField<UploadPurchaseOrderFormSchema>
                    name={"file"}
                    label="Purchase Order File"
                />

                <ArTextAreaFormField<UploadPurchaseOrderFormSchema>
                    label="Description"
                    name="description"
                    placeholder="Enter description"
                    rows={4}
                />

                <ArTextAreaFormField<UploadPurchaseOrderFormSchema>
                    label="Remarks"
                    name="remarks"
                    placeholder="Enter remarks"
                    rows={4}
                />

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Purchase Order"
                              : "Create Purchase Order"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
